import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Plus, Trash2, Send, Loader2, CheckCircle2, Package, History,
  LogIn, QrCode, AlertTriangle, ChevronUp, Download, Lock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function QuickDeclarePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userFeatures, setUserFeatures] = useState(null);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([
    { name: "", batch_number: "", expiry_date: "", quantity: 1, unit_price: 0 }
  ]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/api/subscriptions/my-features`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setUserFeatures(res.data)).catch(() => {});
    }
  }, [token]);

  // If not logged in, show login prompt
  if (!user || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #e8f4f8 0%, #f8fafc 100%)" }}>
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(0,180,216,0.1)" }}>
              <QrCode className="w-8 h-8" style={{ color: "#00B4D8" }} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Déclaration Rapide</h2>
            <p className="text-sm text-slate-500 mb-6">
              Connectez-vous pour declarer vos produits périmés directement depuis votre téléphone.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full py-5 font-semibold"
              style={{ background: "#00B4D8", color: "#fff" }}
              data-testid="qr-login-btn"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if feature is available in user's plan
  const hasAccess = userFeatures?.is_admin || userFeatures?.features?.qr_mobile_declaration;
  if (userFeatures && !hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #e8f4f8 0%, #f8fafc 100%)" }}>
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(255,195,0,0.1)" }}>
              <Lock className="w-8 h-8" style={{ color: "#FFC300" }} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Plan Essentiel requis</h2>
            <p className="text-sm text-slate-500 mb-6">
              La déclaration rapide via QR code est disponible a partir du plan Essentiel. Mettez a niveau pour y acceder.
            </p>
            <Button
              onClick={() => navigate("/subscription")}
              className="w-full py-5 font-semibold"
              style={{ background: "#FFC300", color: "#0f172a" }}
              data-testid="upgrade-plan-btn"
            >
              Voir les abonnements
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const addItem = () => {
    setItems([...items, { name: "", batch_number: "", expiry_date: "", quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const totalValue = items.reduce((sum, it) => sum + (it.quantity * it.unit_price), 0);

  const handleSubmit = async () => {
    const validItems = items.filter(it => it.name.trim());
    if (validItems.length === 0) {
      toast.error("Ajoutez au moins un produit");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/quick-declare`, {
        token: token,
        items: validItems.map(it => ({
          name: it.name.trim(),
          batch_number: it.batch_number.trim(),
          expiry_date: it.expiry_date,
          quantity: parseInt(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
        })),
        notes: notes.trim() || null,
      });
      setResult(res.data);
      setSubmitted(true);
      toast.success(`${res.data.products_count} produit(s) declare(s) !`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la déclaration";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (history.length > 0) {
      setShowHistory(!showHistory);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/déclaration-history?token=${token}`);
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch {
      toast.error("Impossible de charger l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setResult(null);
    setNotes("");
    setItems([{ name: "", batch_number: "", expiry_date: "", quantity: 1, unit_price: 0 }]);
  };

  // Success screen
  if (submitted && result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #e8f4f8 0%, #f0fdf4 100%)" }}>
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="déclaration-success">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(0,180,216,0.1)" }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: "#00B4D8" }} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Déclaration enregistree !</h2>
            <p className="text-sm text-slate-500 mb-4">{result.message}</p>

            <div className="rounded-xl p-4 mb-6 text-left" style={{ background: "rgba(0,180,216,0.06)" }}>
              <div className="flex justify-between mb-2">
                <span className="text-xs text-slate-500">Produits declares</span>
                <span className="text-sm font-bold text-slate-800">{result.products_count}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-xs text-slate-500">Valeur totale</span>
                <span className="text-sm font-bold text-slate-800">{result.total_value?.toLocaleString()} FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Ref. destruction</span>
                <span className="text-xs font-mono" style={{ color: "#00B4D8" }}>#{result.destruction_id?.slice(0, 8)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={resetForm}
                className="w-full py-5 font-semibold"
                style={{ background: "#00B4D8", color: "#fff" }}
                data-testid="new-déclaration-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouvelle déclaration
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full py-5 text-slate-600"
              >
                Voir le tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Déclaration form
  return (
    <div className="min-h-screen pb-8" style={{ background: "linear-gradient(180deg, #e8f4f8 0%, #f8fafc 100%)" }}>
      {/* Header */}
      <div className="sticky top-0 z-20 px-4 py-3 backdrop-blur-lg" style={{ background: "rgba(0,180,216,0.95)" }}>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QrCode className="w-5 h-5" style={{ color: "#FFC300" }} />
            <div>
              <h1 className="text-sm font-bold" style={{ color: "#fff" }}>Déclaration Rapide</h1>
              <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.7)" }}>{user?.pharmacy_name || user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/api/downloads/qr-déclaration-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: "rgba(255,195,0,0.25)", color: "#FFC300" }}
              data-testid="download-qr-pdf-btn"
            >
              <Download className="w-3 h-3" />
              PDF QR
            </a>
            <button
              onClick={loadHistory}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
              data-testid="history-btn"
            >
              {historyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <History className="w-3 h-3" />}
              Historique
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-4">
        {/* History panel */}
        {showHistory && (
          <Card className="mb-4 shadow-md border-0" data-testid="history-panel">
            <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm text-slate-700">Historique des declarations</CardTitle>
              <button onClick={() => setShowHistory(false)}>
                <ChevronUp className="w-4 h-4 text-slate-400" />
              </button>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              {history.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Aucune déclaration</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={h.id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                      <div>
                        <p className="text-xs font-semibold text-slate-700">{h.products_count} produit(s)</p>
                        <p className="text-[10px] text-slate-400">
                          {new Date(h.created_at).toLocaleDateString("fr-FR")} — {h.total_value?.toLocaleString()} FCFA
                        </p>
                      </div>
                      <span className="text-[9px] font-mono px-2 py-0.5 rounded-full" style={{ background: "rgba(0,180,216,0.08)", color: "#00B4D8" }}>
                        {h.source === "qr_mobile" ? "QR" : "Web"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <div className="flex items-start gap-2 p-3 rounded-xl mb-4" style={{ background: "rgba(255,195,0,0.08)", border: "1px solid rgba(255,195,0,0.2)" }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#F59E0B" }} />
          <p className="text-xs text-slate-600 leading-relaxed">
            Declarez vos produits périmés en quelques secondes. Les données seront automatiquement integrees dans vos statistiques et analyses.
          </p>
        </div>

        {/* Product items */}
        <div className="space-y-3">
          {items.map((item, index) => (
            <Card key={index} className="shadow-sm border-0" data-testid={`product-item-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" style={{ color: "#00B4D8" }} />
                    <span className="text-xs font-bold text-slate-700">Produit {index + 1}</span>
                  </div>
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-product-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-2.5">
                  <div>
                    <Label className="text-[11px] text-slate-500">Nom du produit *</Label>
                    <Input
                      placeholder="Ex: Amoxicilline 500mg"
                      value={item.name}
                      onChange={(e) => updateItem(index, "name", e.target.value)}
                      className="h-10 text-sm bg-white border-slate-200"
                      data-testid={`product-name-${index}`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-slate-500">N. Lot</Label>
                      <Input
                        placeholder="LOT-XXX"
                        value={item.batch_number}
                        onChange={(e) => updateItem(index, "batch_number", e.target.value)}
                        className="h-10 text-sm bg-white border-slate-200"
                        data-testid={`product-batch-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Date exp.</Label>
                      <Input
                        type="date"
                        value={item.expiry_date}
                        onChange={(e) => updateItem(index, "expiry_date", e.target.value)}
                        className="h-10 text-sm bg-white border-slate-200"
                        data-testid={`product-expiry-${index}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-slate-500">Quantite</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", e.target.value)}
                        className="h-10 text-sm bg-white border-slate-200"
                        data-testid={`product-qty-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Prix unit. (FCFA)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                        className="h-10 text-sm bg-white border-slate-200"
                        data-testid={`product-price-${index}`}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add product button */}
        <button
          onClick={addItem}
          className="w-full mt-3 py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:bg-slate-50"
          style={{ borderColor: "rgba(0,180,216,0.3)", color: "#00B4D8" }}
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" />
          Ajouter un produit
        </button>

        {/* Notes */}
        <div className="mt-4">
          <Label className="text-[11px] text-slate-500">Notes (optionnel)</Label>
          <textarea
            className="w-full mt-1 p-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 resize-none"
            rows={2}
            placeholder="Observations complementaires..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="déclaration-notes"
          />
        </div>

        {/* Summary & Submit */}
        <div className="mt-4 p-4 rounded-xl" style={{ background: "rgba(0,180,216,0.06)", border: "1px solid rgba(0,180,216,0.15)" }}>
          <div className="flex justify-between mb-1">
            <span className="text-xs text-slate-500">Produits a declarer</span>
            <span className="text-sm font-bold text-slate-800">{items.filter(i => i.name.trim()).length}</span>
          </div>
          <div className="flex justify-between mb-4">
            <span className="text-xs text-slate-500">Valeur estimee</span>
            <span className="text-sm font-bold" style={{ color: "#00B4D8" }}>{totalValue.toLocaleString()} FCFA</span>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading || items.every(i => !i.name.trim())}
            className="w-full py-5 font-semibold text-sm shadow-lg"
            style={{ background: loading ? "#94a3b8" : "#00B4D8", color: "#fff" }}
            data-testid="submit-déclaration-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer la déclaration
              </>
            )}
          </Button>
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-6">
          CMES INTEL-STOCK — Déclaration rapide via QR Code | Tel: 07 68 12 70 00
        </p>
      </div>
    </div>
  );
}

export default QuickDeclarePage;
